import React, { useState, useContext, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';

const VideosClase = () => {
  const { user, API } = useContext(AuthContext);
  const { claseId } = useParams();
  const navigate = useNavigate();
  const [clase, setClase] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditingPlaylist, setIsEditingPlaylist] = useState(false);
  const [playlistUrl, setPlaylistUrl] = useState('');
  const [updating, setUpdating] = useState(false);
  const [accesos, setAccesos] = useState(null);
  const [showAccesos, setShowAccesos] = useState(false);

  // Videos individuales
  const [videosLeccion, setVideosLeccion] = useState([]);
  const [showAddVideo, setShowAddVideo] = useState(false);
  const [nuevoVideo, setNuevoVideo] = useState({ titulo: '', youtube_url: '', numero_leccion: '', fecha_clase: '' });
  const [savingVideo, setSavingVideo] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [registrandoVideo, setRegistrandoVideo] = useState(null);
  const [activeVideoId, setActiveVideoId] = useState(null);
  const [videoProgress, setVideoProgress] = useState({});  // {videoId: percentage}
  const playerRefs = React.useRef({});
  const [showReporte, setShowReporte] = useState(false);
  const [reporte, setReporte] = useState(null);

  const isAdmin = user?.rol === 'admin';
  const isProfesor = user?.rol === 'profesor';
  const isEstudiante = user?.rol === 'estudiante' || (!isAdmin && !isProfesor);

  useEffect(() => {
    loadClase();
    loadVideosLeccion();
  }, [claseId]);

  const loadClase = async () => {
    try {
      const response = await axios.get(`${API}/clases`);
      const claseData = response.data.find(c => c.id === claseId);
      if (!claseData) {
        toast.error('Clase no encontrada');
        navigate('/app/clases');
        return;
      }
      setClase(claseData);
      setPlaylistUrl(claseData.playlist_youtube || '');
    } catch (error) {
      toast.error('Error al cargar información de la clase');
      navigate('/app/clases');
    } finally {
      setLoading(false);
    }
  };

  const loadVideosLeccion = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/clases/${claseId}/videos-leccion`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Ordenar numéricamente por número de lección (1, 2, ..., 13, 14)
      // El backend a veces devuelve orden alfabético ("1","10","11",..,"2") por
      // string-sort de Mongo o inserción cronológica. Forzamos numérico acá.
      const videos = (response.data.videos || []).slice().sort((a, b) => {
        const na = parseInt(a.numero_leccion, 10);
        const nb = parseInt(b.numero_leccion, 10);
        if (isNaN(na) && isNaN(nb)) return 0;
        if (isNaN(na)) return 1;
        if (isNaN(nb)) return -1;
        return na - nb;
      });
      setVideosLeccion(videos);
    } catch (error) {
      console.error('Error cargando videos:', error);
    }
  };

  const getPlaylistId = (url) => {
    if (!url) return null;
    const patterns = [/[?&]list=([a-zA-Z0-9_-]+)/, /playlist\?list=([a-zA-Z0-9_-]+)/];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  };

  const getYoutubeVideoId = (url) => {
    if (!url) return null;
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
      /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  };

  const handleUpdatePlaylist = async () => {
    if (playlistUrl && !playlistUrl.includes('youtube.com') && !playlistUrl.includes('youtu.be')) {
      toast.error('Por favor ingresa una URL válida de YouTube');
      return;
    }
    setUpdating(true);
    try {
      await axios.put(`${API}/clases/${clase.id}/playlist`, { playlist_youtube: playlistUrl });
      toast.success('Playlist actualizada');
      setIsEditingPlaylist(false);
      await loadClase();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar playlist');
    } finally {
      setUpdating(false);
    }
  };

  const handleGuardarVideo = async () => {
    if (!nuevoVideo.titulo.trim()) {
      toast.error('Ingresa un título para la lección');
      return;
    }
    if (!nuevoVideo.youtube_url.trim()) {
      toast.error('Ingresa la URL del video de YouTube');
      return;
    }
    setSavingVideo(true);
    try {
      const token = localStorage.getItem('token');
      if (editingVideo) {
        await axios.put(`${API}/clases/${claseId}/videos-leccion/${editingVideo.id}`, nuevoVideo, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Video actualizado');
      } else {
        await axios.post(`${API}/clases/${claseId}/videos-leccion`, nuevoVideo, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Video agregado');
      }
      setShowAddVideo(false);
      setEditingVideo(null);
      setNuevoVideo({ titulo: '', youtube_url: '', numero_leccion: '', fecha_clase: '' });
      await loadVideosLeccion();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar video');
    } finally {
      setSavingVideo(false);
    }
  };

  const handleEliminarVideo = async (videoId) => {
    if (!window.confirm('¿Estás seguro de eliminar este video?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/clases/${claseId}/videos-leccion/${videoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Video eliminado');
      await loadVideosLeccion();
    } catch (error) {
      toast.error('Error al eliminar video');
    }
  };

  const handleRegistrarAsistenciaVideo = async (video) => {
    // Solo abrir/cerrar el reproductor, la asistencia se registra al 50%
    if (activeVideoId === video.id) {
      // Cerrar video
      if (playerRefs.current[video.id]) {
        clearInterval(playerRefs.current[video.id].interval);
        delete playerRefs.current[video.id];
      }
      setActiveVideoId(null);
    } else {
      setActiveVideoId(video.id);
      if (!video.visto) {
        toast.info('Debes ver al menos el 50% del video para que cuente como asistencia');
      }
    }
  };

  const registrarAsistenciaReal = async (video) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/videos/registrar-acceso`, {
        clase_id: claseId,
        video_id: video.id
      }, { headers: { Authorization: `Bearer ${token}` } });
      setVideosLeccion(prev => prev.map(v => 
        v.id === video.id ? { ...v, visto: true, fecha_visto: new Date().toISOString() } : v
      ));
      toast.success('Asistencia registrada - completaste el 50% del video');
    } catch (error) {
      console.error('Error registrando asistencia:', error);
    }
  };

  // YouTube Player API: rastrear progreso (solo estudiantes)
  const onPlayerReady = (videoId, video) => {
    const player = playerRefs.current[videoId]?.player;
    if (!player) return;
    
    const interval = setInterval(() => {
      try {
        const current = player.getCurrentTime();
        const duration = player.getDuration();
        if (duration > 0) {
          const pct = Math.round((current / duration) * 100);
          setVideoProgress(prev => ({ ...prev, [videoId]: pct }));
          
          // Al llegar a 50%, registrar asistencia
          if (pct >= 50 && !video.visto && !playerRefs.current[videoId]?.registered) {
            playerRefs.current[videoId].registered = true;
            registrarAsistenciaReal(video);
          }
        }
      } catch (e) { /* player destroyed */ }
    }, 2000);
    
    playerRefs.current[videoId].interval = interval;
  };

  // Cargar YouTube IFrame API solo para estudiantes
  useEffect(() => {
    if (!isEstudiante) return;
    if (!window.YT) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      document.body.appendChild(tag);
    }
  }, [isEstudiante]);

  // Crear player cuando se activa un video (solo estudiantes para tracking)
  useEffect(() => {
    if (!activeVideoId || !isEstudiante) return;
    const video = videosLeccion.find(v => v.id === activeVideoId);
    if (!video || video.visto) return;
    const ytVideoId = getYoutubeVideoId(video.youtube_url);
    if (!ytVideoId) return;

    const createPlayer = () => {
      if (!window.YT || !window.YT.Player) {
        setTimeout(createPlayer, 500);
        return;
      }
      const containerId = `yt-player-${activeVideoId}`;
      const iframe = document.getElementById(containerId);
      if (!iframe) {
        setTimeout(createPlayer, 300);
        return;
      }
      
      const player = new window.YT.Player(containerId, {
        events: {
          onReady: () => onPlayerReady(activeVideoId, video)
        }
      });
      playerRefs.current[activeVideoId] = { player, registered: false };
    };
    
    createPlayer();

    return () => {
      if (playerRefs.current[activeVideoId]) {
        clearInterval(playerRefs.current[activeVideoId].interval);
      }
    };
  }, [activeVideoId]);

  const handleEditarVideo = (video) => {
    setEditingVideo(video);
    setNuevoVideo({
      titulo: video.titulo,
      youtube_url: video.youtube_url,
      numero_leccion: video.numero_leccion || '',
      fecha_clase: video.fecha_clase || ''
    });
    setShowAddVideo(true);
  };

  const loadAccesos = async () => {
    try {
      const response = await axios.get(`${API}/videos/accesos/${claseId}`);
      setAccesos(response.data);
      setShowAccesos(true);
    } catch (error) {
      toast.error('Error al cargar registro de accesos');
    }
  };

  const loadReporte = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/clases/${claseId}/videos-leccion/reporte`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReporte(response.data);
      setShowReporte(true);
    } catch (error) {
      toast.error('Error al cargar reporte');
    }
  };

  const formatFecha = (fecha) => {
    if (!fecha) return '';
    const d = new Date(fecha);
    return d.toLocaleDateString('es-UY', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const playlistId = getPlaylistId(clase?.playlist_youtube);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <i className="fas fa-spinner fa-spin text-4xl text-blue-600"></i>
      </div>
    );
  }

  if (!clase) {
    return (
      <div className="text-center py-12">
        <i className="fas fa-exclamation-triangle text-4xl text-gray-400 mb-4"></i>
        <p className="text-gray-600">Clase no encontrada</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="videos-clase-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center space-x-2 text-sm text-gray-500 mb-2">
            <Button variant="ghost" size="sm" onClick={() => navigate('/app/videos-clases')}
              className="text-blue-600 hover:text-blue-800 p-0 h-auto font-normal">
              Videos
            </Button>
            <span>›</span>
            <span className="font-medium text-gray-900">{clase.titulo}</span>
          </div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">
            Videos de Clases
          </h2>
          <p className="text-gray-600 text-sm">
            {clase.titulo}
          </p>
        </div>

        {isAdmin && (
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={() => setIsEditingPlaylist(true)} data-testid="edit-playlist-button">
              <i className="fas fa-list mr-2"></i>Playlist
            </Button>
            <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white" data-testid="add-video-button"
              onClick={() => { setEditingVideo(null); setNuevoVideo({ titulo: '', youtube_url: '', numero_leccion: String(videosLeccion.length + 1), fecha_clase: '' }); setShowAddVideo(true); }}>
              <i className="fas fa-plus mr-2"></i>Agregar Video
            </Button>
          </div>
        )}
      </div>

      {/* Videos Individuales */}
      {videosLeccion.length > 0 && (
        <Card className="shadow-lg border-0" data-testid="videos-leccion-list">
          <CardHeader className="bg-gradient-to-r from-red-50 to-orange-50 pb-3">
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <i className="fab fa-youtube text-red-600 text-xl"></i>
                <span className="text-gray-800">Videos de Lecciones</span>
                <span className="text-sm font-normal text-gray-500">({videosLeccion.length})</span>
              </div>
              {(isAdmin || isProfesor) && (
                <Button variant="outline" size="sm" onClick={loadReporte} data-testid="ver-reporte-btn">
                  <i className="fas fa-chart-bar mr-2"></i>Reporte
                </Button>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y">
              {videosLeccion.map((video) => {
                const videoId = getYoutubeVideoId(video.youtube_url);
                const thumbnailUrl = videoId ? `https://img.youtube.com/vi/${videoId}/mqdefault.jpg` : null;
                
                return (
                  <div key={video.id} data-testid={`video-item-${video.id}`}>
                    <div className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 hover:bg-gray-50 transition-colors">
                    {/* Thumbnail */}
                    <div className="relative flex-shrink-0 w-24 sm:w-32 h-16 sm:h-20 rounded-lg overflow-hidden bg-gray-200">
                      {thumbnailUrl ? (
                        <img src={thumbnailUrl} alt={video.titulo} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gray-800">
                          <i className="fab fa-youtube text-red-500 text-2xl"></i>
                        </div>
                      )}
                      {/* Badge de número */}
                      <div className="absolute top-1 left-1 bg-black/70 text-white text-xs px-1.5 py-0.5 rounded font-bold">
                        {video.numero_leccion || '?'}
                      </div>
                      {/* Check de visto */}
                      {video.visto && (
                        <div className="absolute bottom-1 right-1 bg-green-500 text-white rounded-full w-5 h-5 flex items-center justify-center">
                          <i className="fas fa-check text-xs"></i>
                        </div>
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-gray-800 text-sm sm:text-base truncate">{video.titulo}</h4>
                      <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                        <span>Lección {video.numero_leccion}</span>
                        {video.fecha_clase && <span>{formatFecha(video.fecha_clase)}</span>}
                        {video.visto && (
                          <span className="text-green-600 font-medium">
                            <i className="fas fa-check-circle mr-1"></i>Visto
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button size="sm"
                        className={activeVideoId === video.id ? "bg-gray-600 hover:bg-gray-700 text-white" : video.visto && isEstudiante ? "bg-green-600 hover:bg-green-700 text-white" : "bg-red-600 hover:bg-red-700 text-white"}
                        onClick={() => handleRegistrarAsistenciaVideo(video)}
                        data-testid={`ver-video-${video.id}`}>
                        {activeVideoId === video.id ? (
                          <><i className="fas fa-times mr-1"></i><span className="hidden sm:inline">Cerrar</span></>
                        ) : (
                          <><i className="fas fa-play mr-1"></i><span className="hidden sm:inline">{video.visto && isEstudiante ? 'Ver de Nuevo' : 'Ver Video'}</span></>
                        )}
                      </Button>
                      {isAdmin && (
                        <>
                          <Button size="sm" variant="ghost" onClick={() => handleEditarVideo(video)}>
                            <i className="fas fa-edit text-blue-600"></i>
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleEliminarVideo(video.id)}>
                            <i className="fas fa-trash text-red-500"></i>
                          </Button>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Reproductor embebido */}
                  {activeVideoId === video.id && videoId && (
                    <div className="px-3 pb-3">
                      {/* Barra de progreso hacia asistencia - solo estudiantes */}
                      {isEstudiante && !video.visto && (
                        <div className="mb-2">
                          <div className="flex items-center justify-between text-xs mb-1">
                            <span className="text-gray-600">Progreso para asistencia</span>
                            <span className={`font-bold ${(videoProgress[video.id] || 0) >= 50 ? 'text-green-600' : 'text-orange-500'}`}>
                              {videoProgress[video.id] || 0}% / 50% requerido
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className={`h-2 rounded-full transition-all duration-500 ${(videoProgress[video.id] || 0) >= 50 ? 'bg-green-500' : 'bg-orange-400'}`}
                              style={{ width: `${Math.min(videoProgress[video.id] || 0, 100)}%` }}
                            />
                          </div>
                        </div>
                      )}
                      {isEstudiante && video.visto && (
                        <div className="mb-2 flex items-center text-xs text-green-600 font-medium">
                          <i className="fas fa-check-circle mr-1"></i>
                          Asistencia ya registrada
                        </div>
                      )}
                      <div className="aspect-video relative rounded-lg overflow-hidden bg-black">
                        <iframe
                          id={isEstudiante && !video.visto ? `yt-player-${video.id}` : undefined}
                          src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1${isEstudiante && !video.visto ? '&enablejsapi=1&origin=' + window.location.origin : ''}`}
                          className="w-full h-full"
                          allowFullScreen
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          title={video.titulo}
                        />
                      </div>
                    </div>
                  )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty State when no videos */}
      {videosLeccion.length === 0 && !clase?.playlist_youtube && (
        <Card className="shadow-lg border-0">
          <CardContent className="text-center py-12">
            <i className="fab fa-youtube text-gray-300 text-6xl mb-6"></i>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No hay videos disponibles</h3>
            <p className="text-gray-600 mb-6">
              {isEstudiante
                ? 'Aún no se han agregado videos para esta clase.'
                : 'Agrega videos de YouTube para cada lección.'}
            </p>
            {isAdmin && (
              <Button className="bg-red-600 hover:bg-red-700 text-white"
                onClick={() => { setNuevoVideo({ titulo: '', youtube_url: '', numero_leccion: '1', fecha_clase: '' }); setShowAddVideo(true); }}>
                <i className="fas fa-plus mr-2"></i>Agregar Primer Video
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Playlist embed (colapsable) */}
      {clase?.playlist_youtube && playlistId && (
        <details className="group">
          <summary className="cursor-pointer bg-gray-50 border rounded-xl p-4 flex items-center justify-between hover:bg-gray-100 transition-colors">
            <div className="flex items-center gap-3">
              <i className="fab fa-youtube text-red-600 text-xl"></i>
              <span className="font-semibold text-gray-800">Playlist Completa de YouTube</span>
            </div>
            <i className="fas fa-chevron-down group-open:rotate-180 transition-transform text-gray-500"></i>
          </summary>
          <Card className="shadow-lg border-0 overflow-hidden mt-2">
            <CardContent className="p-0">
              <div className="aspect-video bg-black">
                <iframe
                  src={`https://www.youtube.com/embed/videoseries?list=${playlistId}&rel=0&modestbranding=1`}
                  title="Videos de la Clase"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                ></iframe>
              </div>
            </CardContent>
          </Card>
        </details>
      )}

      {/* Student progress indicator */}
      {isEstudiante && videosLeccion.length > 0 && (
        <Card className="shadow-md border-0 bg-blue-50 border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <i className="fas fa-chart-pie text-blue-600"></i>
                <div>
                  <p className="font-semibold text-blue-800">Tu progreso</p>
                  <p className="text-sm text-blue-600">
                    {videosLeccion.filter(v => v.visto).length} de {videosLeccion.length} videos vistos
                  </p>
                </div>
              </div>
              <div className="w-16 h-16 relative">
                <svg className="w-16 h-16 transform -rotate-90" viewBox="0 0 36 36">
                  <path d="M18 2.0845a15.9155 15.9155 0 0 1 0 31.831a15.9155 15.9155 0 0 1 0-31.831" fill="none" stroke="#dbeafe" strokeWidth="3" />
                  <path d="M18 2.0845a15.9155 15.9155 0 0 1 0 31.831a15.9155 15.9155 0 0 1 0-31.831" fill="none" stroke="#2563eb" strokeWidth="3"
                    strokeDasharray={`${(videosLeccion.filter(v => v.visto).length / videosLeccion.length * 100).toFixed(0)}, 100`} />
                </svg>
                <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-blue-700">
                  {Math.round(videosLeccion.filter(v => v.visto).length / videosLeccion.length * 100)}%
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Reporte de asistencia por video */}
      {showReporte && reporte && (
        <Card className="shadow-lg border-0" data-testid="reporte-videos">
          <CardHeader className="bg-gradient-to-r from-indigo-50 to-purple-50">
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <i className="fas fa-chart-bar text-indigo-600"></i>
                <span className="text-indigo-800">Reporte de Asistencia a Videos</span>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowReporte(false)}>
                <i className="fas fa-times"></i>
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-6">
            {/* Resumen por video */}
            <div>
              <h4 className="font-semibold text-gray-700 mb-3">Por Lección</h4>
              <div className="space-y-2">
                {reporte.videos.map(v => (
                  <div key={v.video_id} className="flex items-center gap-3 text-sm">
                    <span className="bg-gray-200 text-gray-700 px-2 py-0.5 rounded font-bold text-xs w-8 text-center">{v.numero_leccion}</span>
                    <span className="flex-1 truncate text-gray-800">{v.titulo}</span>
                    <div className="flex items-center gap-2">
                      <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div className="h-full bg-green-500 rounded-full" style={{ width: `${v.porcentaje}%` }}></div>
                      </div>
                      <span className="text-gray-600 w-16 text-right">{v.total_vistos}/{v.total_inscritos}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Resumen por estudiante */}
            <div>
              <h4 className="font-semibold text-gray-700 mb-3">Por Estudiante</h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left p-2">Estudiante</th>
                      <th className="text-center p-2">Videos Vistos</th>
                      <th className="text-center p-2">%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reporte.estudiantes.map(e => (
                      <tr key={e.estudiante_id} className="border-b hover:bg-gray-50">
                        <td className="p-2">
                          <p className="font-medium text-gray-800">{e.nombre}</p>
                          <p className="text-xs text-gray-500">{e.email}</p>
                        </td>
                        <td className="p-2 text-center">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                            e.porcentaje >= 80 ? 'bg-green-100 text-green-700' :
                            e.porcentaje >= 50 ? 'bg-yellow-100 text-yellow-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {e.videos_vistos}/{e.total_videos}
                          </span>
                        </td>
                        <td className="p-2 text-center text-gray-600">{e.porcentaje}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Dialog: Agregar/Editar Video */}
      <Dialog open={showAddVideo} onOpenChange={(open) => { if (!open) { setShowAddVideo(false); setEditingVideo(null); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingVideo ? 'Editar Video' : 'Agregar Video de Lección'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-4 gap-3">
              <div className="col-span-1">
                <Label htmlFor="numero_leccion">Lección #</Label>
                <Input id="numero_leccion" type="number" min="1" max="20"
                  value={nuevoVideo.numero_leccion}
                  onChange={(e) => setNuevoVideo({ ...nuevoVideo, numero_leccion: e.target.value })}
                  placeholder="1" data-testid="video-numero-input" />
              </div>
              <div className="col-span-3">
                <Label htmlFor="titulo_video">Título</Label>
                <Input id="titulo_video"
                  value={nuevoVideo.titulo}
                  onChange={(e) => setNuevoVideo({ ...nuevoVideo, titulo: e.target.value })}
                  placeholder="Ej: Introducción al Antiguo Testamento" data-testid="video-titulo-input" />
              </div>
            </div>
            <div>
              <Label htmlFor="youtube_url">URL de YouTube</Label>
              <Input id="youtube_url"
                value={nuevoVideo.youtube_url}
                onChange={(e) => setNuevoVideo({ ...nuevoVideo, youtube_url: e.target.value })}
                placeholder="https://www.youtube.com/watch?v=..." data-testid="video-url-input" />
              <p className="text-xs text-gray-500 mt-1">Pega el link del video de YouTube</p>
            </div>
            <div>
              <Label htmlFor="fecha_clase">Fecha de la clase (opcional)</Label>
              <Input id="fecha_clase" type="date"
                value={nuevoVideo.fecha_clase}
                onChange={(e) => setNuevoVideo({ ...nuevoVideo, fecha_clase: e.target.value })}
                data-testid="video-fecha-input" />
            </div>

            {/* Preview */}
            {nuevoVideo.youtube_url && getYoutubeVideoId(nuevoVideo.youtube_url) && (
              <div className="rounded-lg overflow-hidden">
                <img src={`https://img.youtube.com/vi/${getYoutubeVideoId(nuevoVideo.youtube_url)}/mqdefault.jpg`}
                  alt="Preview" className="w-full h-40 object-cover" />
              </div>
            )}

            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => { setShowAddVideo(false); setEditingVideo(null); }} disabled={savingVideo}>
                Cancelar
              </Button>
              <Button onClick={handleGuardarVideo} disabled={savingVideo} className="bg-red-600 hover:bg-red-700 text-white" data-testid="save-video-button">
                {savingVideo ? <><i className="fas fa-spinner fa-spin mr-2"></i>Guardando...</> : <><i className="fas fa-save mr-2"></i>{editingVideo ? 'Actualizar' : 'Guardar'}</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog: Playlist */}
      <Dialog open={isEditingPlaylist} onOpenChange={setIsEditingPlaylist}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Gestionar Playlist de YouTube</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="playlist_url">URL de Playlist de YouTube</Label>
              <Input id="playlist_url" value={playlistUrl}
                onChange={(e) => setPlaylistUrl(e.target.value)}
                placeholder="https://www.youtube.com/playlist?list=..." data-testid="playlist-url-input" />
              <p className="text-xs text-gray-500">Pega aquí la URL completa de la playlist de YouTube</p>
            </div>
            <div className="flex justify-end space-x-3">
              <Button variant="outline" onClick={() => { setIsEditingPlaylist(false); setPlaylistUrl(clase?.playlist_youtube || ''); }} disabled={updating}>
                Cancelar
              </Button>
              <Button onClick={handleUpdatePlaylist} disabled={updating} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-playlist-button">
                {updating ? <><i className="fas fa-spinner fa-spin mr-2"></i>Guardando...</> : <><i className="fas fa-save mr-2"></i>Guardar</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Video Access Log (legacy - general) */}
      {(isAdmin || isProfesor) && videosLeccion.length === 0 && (
        <Card className="shadow-lg border-0" data-testid="video-access-log">
          <CardHeader className="bg-gradient-to-r from-indigo-50 to-purple-50">
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <i className="fas fa-eye text-indigo-600"></i>
                <span className="text-indigo-800">Registro de Acceso General</span>
              </div>
              <Button variant="outline" size="sm" onClick={loadAccesos}
                className="text-indigo-600 border-indigo-300 hover:bg-indigo-50" data-testid="load-accesos-btn">
                <i className={`fas ${showAccesos ? 'fa-sync' : 'fa-chart-bar'} mr-2`}></i>
                {showAccesos ? 'Actualizar' : 'Ver Registros'}
              </Button>
            </CardTitle>
          </CardHeader>
          {showAccesos && accesos && (
            <CardContent className="p-4">
              <div className="flex gap-4 mb-4">
                <div className="bg-indigo-100 rounded-lg p-3 flex-1 text-center">
                  <p className="text-2xl font-bold text-indigo-700">{accesos.total_estudiantes}</p>
                  <p className="text-xs text-indigo-600">Estudiantes</p>
                </div>
                <div className="bg-purple-100 rounded-lg p-3 flex-1 text-center">
                  <p className="text-2xl font-bold text-purple-700">{accesos.total_accesos}</p>
                  <p className="text-xs text-purple-600">Total Accesos</p>
                </div>
              </div>
              {accesos.estudiantes.length === 0 ? (
                <p className="text-gray-500 text-center py-4">Ningún estudiante ha accedido aún</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left p-2">Estudiante</th>
                        <th className="text-center p-2">Veces</th>
                      </tr>
                    </thead>
                    <tbody>
                      {accesos.estudiantes.map((est) => (
                        <tr key={est.estudiante_id} className="border-b hover:bg-gray-50">
                          <td className="p-2">
                            <p className="font-medium text-gray-800">{est.estudiante_nombre}</p>
                          </td>
                          <td className="p-2 text-center">
                            <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded-full text-xs font-bold">
                              {est.total_accesos}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          )}
        </Card>
      )}
    </div>
  );
};

export default VideosClase;
